import { type ToolSpec, n, s, fmt } from "./types";

export const TECH_TOOLS: ToolSpec[] = [
  {
    slug: "gaming-mouse-edpi-calculator",
    cluster: "tech",
    name: "Gaming Mouse eDPI & Sensitivity Converter",
    h1: "FPS Gaming Mouse eDPI & cm/360 Sensitivity Distance Converter",
    title: "Gaming Mouse eDPI & cm/360 Converter | Free (2026)",
    description: "Calculate true eDPI and cm/360 turn distance instantly. Free online gaming mouse sensitivity converter with live formulas and a step-by-step breakdown.",
    keywords: [
      "gaming mouse edpi calculator",
      "cm 360 sensitivity converter",
      "convert valorant sensitivity to cs2",
      "true edpi apex legends calculator",
      "mouse sensitivity distance calculator",
      "cross game sensitivity converter",
    ],
    inputs: [
      { id: "dpi", label: "Mouse DPI", type: "select", def: "800", options: [
        { value: "400", label: "400" }, { value: "800", label: "800" }, { value: "1600", label: "1600" }, { value: "3200", label: "3200" },
      ] },
      { id: "sens", label: "In-game sensitivity", type: "number", def: "0.4", min: 0.001, step: 0.001 },
      { id: "game", label: "Source game", type: "select", def: "cs2", options: [
        { value: "cs2", label: "CS2 / Source" },
        { value: "valorant", label: "Valorant" },
        { value: "apex", label: "Apex Legends" },
        { value: "overwatch", label: "Overwatch 2" },
      ] },
    ],
    compute: (v) => {
      const dpi = n(v, "dpi", 800), sens = n(v, "sens", 0.4), game = s(v, "game");
      const YAW: Record<string, number> = { cs2: 0.022, valorant: 0.07, apex: 0.022, overwatch: 0.0066 };
      const yaw = YAW[game] ?? 0.022;
      const edpi = dpi * sens;
      const denom = dpi * sens * yaw;
      const cm360 = denom > 0 ? (360 * 2.54) / denom : 0;
      const in360 = cm360 / 2.54;
      // Equivalent sens in game B keeps cm/360 identical: sensB = sens × (yawSource / yawB)
      const toGame = (targetYaw: number) => (targetYaw > 0 ? sens * (yaw / targetYaw) : 0);
      const cs2Sens = toGame(YAW.cs2);
      const valSens = toGame(YAW.valorant);
      const apexSens = toGame(YAW.apex);
      const owSens = toGame(YAW.overwatch);
      return {
        cards: [
          { label: "Effective DPI (eDPI)", value: fmt(edpi, 0), sub: "DPI × in-game sensitivity", accent: true },
          { label: "cm / 360°", value: `${fmt(cm360, 2)} cm`, sub: `${fmt(in360, 2)} in / 360°` },
          { label: "Equivalent CS2 sens", value: fmt(cs2Sens, 4), sub: `At ${fmt(dpi, 0)} DPI` },
          { label: "Equivalent Valorant sens", value: fmt(valSens, 4), sub: `At ${fmt(dpi, 0)} DPI` },
        ],
        table: {
          title: "Cross-game sensitivity matrix",
          head: ["Game", "Equivalent sensitivity", "Yaw constant"],
          rows: [
            ["CS2 / Source", fmt(cs2Sens, 4), "0.022"],
            ["Valorant", fmt(valSens, 4), "0.07"],
            ["Apex Legends", fmt(apexSens, 4), "0.022"],
            ["Overwatch 2", fmt(owSens, 4), "0.0066"],
          ],
        },
        summary: `At ${fmt(dpi, 0)} DPI and ${fmt(sens, 3)} sensitivity your eDPI is ${fmt(edpi, 0)}, needing ${fmt(cm360, 2)} cm of mouse movement for a full 360° turn.`,
      };
    },
    intro: "eDPI (effective DPI) is the true sensitivity number that lets you compare mouse settings across games and hardware, since raw in-game sensitivity sliders mean nothing without knowing the DPI behind them. This calculator converts your DPI and in-game sensitivity into eDPI, physical cm/360° turn distance, and equivalent settings in other popular titles.",
    sections: [
      { h: "Why eDPI matters more than raw sensitivity", body: "Two players with wildly different in-game sensitivity sliders can have identical aim feel if their DPI × sensitivity (eDPI) matches. Pros often share eDPI rather than raw sensitivity because it normalizes for different mice and settings menus." },
      { h: "Understanding cm/360°", body: "cm/360° measures the actual physical mousepad distance needed to spin a full circle in-game. Lower values mean faster, twitchier turns; competitive FPS players commonly sit between 20-40 cm/360° depending on playstyle and mousepad size." },
      { h: "Each game's yaw constant explained", body: "Games apply different internal multipliers (yaw) to convert raw mouse counts into camera rotation. Valorant's yaw of 0.07 is much higher than CS2's 0.022, meaning the same eDPI produces very different turn speeds unless converted properly." },
      { h: "Using this to switch games without relearning aim", body: "When switching from one FPS to another, matching cm/360° (not the raw sensitivity number) preserves your muscle memory. Use the cross-game table to find the number to type into your new game's settings menu." },
    ],
    staticTable: {
      title: "Common competitive eDPI ranges",
      head: ["Playstyle", "Typical eDPI", "Typical cm/360°"],
      rows: [
        ["Low sens sniper/AWP", "400-800", "50-70 cm"],
        ["Balanced rifler", "800-1600", "25-40 cm"],
        ["High sens flick shooter", "1600-3200", "10-20 cm"],
        ["Valorant average pro", "~320", "~30 cm"],
        ["CS2 average pro", "~800", "~32 cm"],
        ["Console-style controller feel", "3200+", "<10 cm"],
      ],
    },
    faqs: [
      { q: "What is eDPI?", a: "Effective DPI is your mouse's hardware DPI multiplied by your in-game sensitivity multiplier, giving a single number comparable across different games and mice." },
      { q: "What cm/360° do pros use?", a: "Most professional FPS players sit between 25-40 cm/360°, though snipers and AWPers often go higher (50-70cm) for precision at long range." },
      { q: "Why does the same eDPI feel different in two games?", a: "Each game engine applies a different yaw (degrees rotated per count), so identical eDPI values produce different actual turn speeds unless you convert using each game's yaw constant." },
      { q: "Should I raise my DPI or my in-game sensitivity?", a: "Higher DPI with lower in-game sensitivity generally gives smoother, more precise tracking due to better mouse polling resolution, so many pros prefer 400-800 DPI with a higher game multiplier." },
      { q: "How do I convert my Valorant sensitivity to CS2?", a: "Enter your Valorant DPI and sensitivity, then read the equivalent CS2 sensitivity from the cross-game table — it preserves your exact cm/360° distance." },
      { q: "What DPI should I use for FPS games?", a: "400-1600 DPI covers nearly all competitive setups; higher raw DPI without adjusting sensitivity down usually just adds unnecessary mouse jitter." },
      { q: "Does mousepad size limit my sensitivity choice?", a: "Yes — a lower cm/360° needs a larger pad to complete full turns without lifting the mouse; if you have a small pad, a higher cm/360° may fit better." },
      { q: "Is lower sensitivity always more accurate?", a: "Not universally — very low sensitivity requires more arm movement and can slow reaction to close-range flicks, so accuracy depends on practiced consistency, not just a low number." },
      { q: "Why do Overwatch and Valorant feel so different at the same DPI?", a: "Their yaw constants differ by more than 10x, so identical DPI and sensitivity sliders produce very different physical turn distances between the two games." },
      { q: "Can I use this for console controller sensitivity?", a: "No, this converter is built for mouse DPI-based yaw math; controller sensitivity uses stick response curves that work on a completely different system." },
    ],
    formula: {
      expressions: [
        "eDPI = DPI × In-Game Sensitivity",
        "cm/360 = (360 × 2.54) / (DPI × Sens × Yaw)",
        "Equivalent Sens (Game B) = (eDPI / DPI) × (YawA / YawB)",
      ],
      variables: [
        { name: "DPI", meaning: "Hardware mouse counts per inch" },
        { name: "Yaw", meaning: "Game-specific degrees rotated per count" },
      ],
    },
    related: ["monitor-viewing-distance-fov-calculator", "speaker-wire-gauge-loss-calculator", "pcb-trace-width-calculator", "drone-lipo-flight-time-calculator"],
  },

  {
    slug: "monitor-viewing-distance-fov-calculator",
    cluster: "tech",
    name: "Monitor Viewing Distance & FOV",
    h1: "Gaming Monitor Viewing Distance vs Field of View (FOV) & Pixel Density",
    title: "Monitor Viewing Distance & FOV Calculator | Free (2026)",
    description: "Calculate FOV degrees, PPI and seating distance instantly. Free online monitor viewing distance calculator with live formulas and a step-by-step breakdown.",
    keywords: [
      "monitor viewing distance calculator",
      "field of view fov calculator gaming",
      "ideal seating distance 27 inch monitor",
      "monitor pixel density ppi calculator",
      "ultrawide fov distance calculator",
      "smpte thx viewing distance standard",
    ],
    inputs: [
      { id: "diagonal", label: "Screen diagonal", type: "number", def: "27", unit: "in", min: 10, max: 100, step: 0.5 },
      { id: "aspect", label: "Aspect ratio", type: "select", def: "16:9", options: [
        { value: "16:9", label: "16:9" }, { value: "21:9", label: "21:9 ultrawide" }, { value: "32:9", label: "32:9 super ultrawide" },
      ] },
      { id: "resolution", label: "Native resolution", type: "select", def: "1440p", options: [
        { value: "1080p", label: "1080p" }, { value: "1440p", label: "1440p" }, { value: "4k", label: "4K" },
      ] },
      { id: "distance", label: "Seating distance", type: "number", def: "30", unit: "in", min: 5, max: 120, step: 1 },
    ],
    compute: (v) => {
      const diagonal = n(v, "diagonal", 27), aspect = s(v, "aspect"), resolution = s(v, "resolution"), distance = n(v, "distance", 30);
      const ratios: Record<string, [number, number]> = { "16:9": [16, 9], "21:9": [21, 9], "32:9": [32, 9] };
      const [aw, ah] = ratios[aspect] || [16, 9];
      const diag2 = Math.sqrt(aw * aw + ah * ah);
      const width = (diagonal * aw) / diag2;
      const height = (diagonal * ah) / diag2;
      const hFovDeg = distance > 0 ? 2 * Math.atan((width / 2) / distance) * (180 / Math.PI) : 0;
      const vFovDeg = distance > 0 ? 2 * Math.atan((height / 2) / distance) * (180 / Math.PI) : 0;
      const resMap: Record<string, [number, number]> = { "1080p": [1920, 1080], "1440p": [2560, 1440], "4k": [3840, 2160] };
      const [rw, rh] = resMap[resolution] || [2560, 1440];
      const ppi = diagonal > 0 ? Math.sqrt(rw * rw + rh * rh) / diagonal : 0;
      const smpte = hFovDeg >= 30 ? "Meets SMPTE cinema standard (30°+)" : "Below SMPTE 30° reference";
      const thx = hFovDeg >= 40 ? "Meets THX immersive standard (40°+)" : "Below THX 40° immersion target";
      const retinaDistance = ppi > 0 ? (1 / (ppi * Math.tan((1 / 60) * (Math.PI / 180)))) : 0;
      return {
        cards: [
          { label: "Horizontal FOV", value: `${fmt(hFovDeg, 1)}°`, sub: hFovDeg >= 40 ? "Highly immersive" : "Moderate immersion", accent: true },
          { label: "Vertical FOV", value: `${fmt(vFovDeg, 1)}°`, sub: `Screen height: ${fmt(height, 1)}in` },
          { label: "Pixel density", value: `${fmt(ppi, 0)} PPI`, sub: `${rw}×${rh} resolution` },
          { label: "Retina limit distance", value: `${fmt(retinaDistance, 1)} in`, sub: "Below this, pixels become visible" },
        ],
        table: {
          title: "Viewing setup breakdown",
          head: ["Metric", "Value"],
          rows: [
            ["Screen width", `${fmt(width, 1)} in`],
            ["Screen height", `${fmt(height, 1)} in`],
            ["Seating distance", `${fmt(distance, 1)} in`],
            ["Horizontal FOV", `${fmt(hFovDeg, 1)}°`],
            ["SMPTE compliance", smpte],
            ["THX compliance", thx],
            ["Pixel density", `${fmt(ppi, 0)} PPI`],
          ],
        },
        summary: `At ${fmt(distance, 0)}in your ${diagonal}in ${aspect} display fills ${fmt(hFovDeg, 1)}° of horizontal field of view — ${thx}.`,
      };
    },
    intro: "Field of view depends as much on seating distance as it does on screen size, so a 27-inch monitor sat too far away can feel smaller than a 24-inch one placed closer. This calculator turns your diagonal size, aspect ratio, resolution, and seating distance into real FOV degrees, pixel density, and cinema-standard compliance ratings.",
    sections: [
      { h: "How screen size and distance combine into FOV", body: "Field of view is a geometric function of screen width divided by viewing distance, run through an arctangent. Doubling the screen width without changing distance roughly doubles perceived FOV, while doubling distance halves it — distance usually matters more than size alone." },
      { h: "SMPTE and THX immersion standards", body: "SMPTE recommends at least a 30° horizontal FOV for reference monitoring, while THX targets 40° for a cinematic home theater feel. Competitive gamers often push past 40° for peripheral awareness, while productivity users prioritize lower FOV to reduce neck movement." },
      { h: "Pixel density and the retina limit", body: "PPI (pixels per inch) determines how close you can sit before individual pixels become visible. Higher resolution panels support closer seating without visible pixelation, which matters more for ultrawide and 4K setups used at desk distances." },
      { h: "Choosing distance for ultrawide and super ultrawide monitors", body: "21:9 and 32:9 panels are much wider than tall, so the horizontal FOV grows dramatically as distance shrinks — but peripheral text can become hard to read if you sit too close, so balance immersion against usability." },
    ],
    staticTable: {
      title: "Recommended seating distance by monitor size",
      head: ["Screen size", "Resolution", "Ideal distance for 40° FOV"],
      rows: [
        ["24in 16:9", "1080p", "~22 in"],
        ["27in 16:9", "1440p", "~25 in"],
        ["32in 16:9", "4K", "~29 in"],
        ["34in 21:9", "1440p", "~26 in"],
        ["38in 21:9", "4K", "~29 in"],
        ["49in 32:9", "1440p (dual)", "~34 in"],
      ],
    },
    faqs: [
      { q: "What FOV is considered immersive for gaming?", a: "Most gamers find 35-45° horizontal FOV immersive without feeling cramped; competitive players may push toward 50°+ using ultrawide monitors sat closer." },
      { q: "How far should I sit from a 27-inch 1440p monitor?", a: "Roughly 24-28 inches gives a good balance between the THX 40° immersion target and comfortable text readability for desk work." },
      { q: "Does higher resolution let me sit closer?", a: "Yes, higher PPI from 4K or 1440p panels means pixels are smaller and less visible up close, so you can safely sit nearer without seeing pixel structure." },
      { q: "What is the SMPTE standard exactly?", a: "SMPTE recommends roughly 30° of horizontal viewing angle for accurate color and framing reference in professional video monitoring setups." },
      { q: "Why does my ultrawide feel less immersive than expected?", a: "If seating distance is too far, even a very wide screen produces a modest FOV — try sitting a few inches closer to unlock the extra field of view ultrawide panels are designed to deliver." },
      { q: "Is more FOV always better?", a: "Not for productivity — excessive FOV means more head and eye movement to read edges of the screen, which can cause fatigue during long work sessions." },
      { q: "What is the retina limit distance?", a: "It is the closest distance at which the human eye can no longer resolve individual pixels, calculated from the display's pixel density (PPI)." },
      { q: "How does aspect ratio affect FOV differently from size?", a: "Wider aspect ratios (21:9, 32:9) increase horizontal FOV without adding vertical FOV, which is why ultrawide panels feel more panoramic than boxy immersive." },
      { q: "Should I match FOV settings in-game to my physical FOV?", a: "Many competitive players do match their in-game FOV slider to their calculated physical FOV to reduce visual distortion and disorientation." },
      { q: "Does curved vs flat panel change this calculation?", a: "This calculator assumes a flat panel; curved monitors slightly increase effective peripheral FOV at the edges beyond what flat geometry predicts." },
    ],
    formula: {
      expressions: [
        "Width = (Diagonal × AspectW) / √(AspectW² + AspectH²)",
        "FOV(deg) = 2 × atan((Width/2) / Distance) × (180/π)",
        "PPI = √(ResW² + ResH²) / Diagonal",
      ],
      variables: [
        { name: "AspectW/H", meaning: "Aspect ratio width/height components" },
        { name: "Distance", meaning: "Seating distance from screen" },
      ],
    },
    related: ["gaming-mouse-edpi-calculator", "speaker-wire-gauge-loss-calculator", "pcb-trace-width-calculator", "drone-lipo-flight-time-calculator"],
  },

  {
    slug: "speaker-wire-gauge-loss-calculator",
    cluster: "tech",
    name: "Speaker Wire Gauge & Loss",
    h1: "Home Theater Speaker Wire Gauge (AWG) & Signal Power Loss Sizer",
    title: "Speaker Wire Gauge & Power Loss Calculator | Free (2026)",
    description: "Calculate speaker cable resistance and power loss instantly. Free online speaker wire gauge calculator with live formulas and a step-by-step breakdown.",
    keywords: [
      "speaker wire gauge calculator",
      "speaker wire power loss calculator",
      "what gauge speaker wire for 50 feet",
      "speaker cable resistance calculator",
      "awg speaker wire distance chart",
      "damping factor speaker wire calculator",
    ],
    inputs: [
      { id: "impedance", label: "Speaker impedance", type: "select", def: "8", options: [
        { value: "4", label: "4 Ohm" }, { value: "6", label: "6 Ohm" }, { value: "8", label: "8 Ohm" },
      ] },
      { id: "length", label: "Cable length (one-way)", type: "number", def: "50", unit: "ft", min: 1, step: 1 },
      { id: "gauge", label: "Wire gauge (AWG)", type: "select", def: "14", options: [
        { value: "12", label: "12 AWG" }, { value: "14", label: "14 AWG" }, { value: "16", label: "16 AWG" }, { value: "18", label: "18 AWG" },
      ] },
      { id: "power", label: "Amplifier RMS power", type: "number", def: "100", unit: "W", min: 1, step: 1 },
    ],
    compute: (v) => {
      const impedance = n(v, "impedance", 8), length = n(v, "length", 50), gauge = s(v, "gauge"), power = n(v, "power", 100);
      const resistPerFoot: Record<string, number> = { "12": 0.00159, "14": 0.00253, "16": 0.00402, "18": 0.00639 };
      const rPerFt = resistPerFoot[gauge] ?? 0.00253;
      const loopR = 2 * length * rPerFt;
      const resistRatio = impedance > 0 ? (loopR / impedance) * 100 : 0;
      const totalR = impedance + loopR;
      const powerLossDb = totalR > 0 ? 10 * Math.log10(impedance / totalR) : 0;
      const wattsAtSpeaker = totalR > 0 ? power * (impedance / totalR) : 0;
      const wattsLost = power - wattsAtSpeaker;
      const pctLost = power > 0 ? (wattsLost / power) * 100 : 0;
      const safe = resistRatio <= 5;
      return {
        cards: [
          { label: "Loop resistance", value: `${fmt(loopR, 3)} Ω`, sub: `${gauge} AWG over ${fmt(length, 0)}ft`, accent: true },
          { label: "Power loss", value: `${fmt(powerLossDb, 2)} dB`, sub: `${fmt(pctLost, 1)}% wattage wasted as heat` },
          { label: "Watts delivered to speaker", value: `${fmt(wattsAtSpeaker, 1)} W`, sub: `Of ${fmt(power, 0)}W amp output` },
          { label: "5% standard", value: safe ? "Pass" : "Fail", sub: `Resistance is ${fmt(resistRatio, 1)}% of impedance` },
        ],
        table: {
          title: "Wire loss breakdown",
          head: ["Metric", "Value"],
          rows: [
            ["Speaker impedance", `${impedance} Ω`],
            ["Wire gauge", `${gauge} AWG`],
            ["One-way length", `${fmt(length, 0)} ft`],
            ["Loop resistance", `${fmt(loopR, 3)} Ω`],
            ["Resistance as % of impedance", `${fmt(resistRatio, 1)}%`],
            ["Power loss", `${fmt(powerLossDb, 2)} dB`],
            ["Watts lost to heat", `${fmt(wattsLost, 1)} W`],
          ],
        },
        summary: safe
          ? `${gauge} AWG over ${fmt(length, 0)}ft is safe — only ${fmt(pctLost, 1)}% of amplifier power is lost in the cable.`
          : `${gauge} AWG over ${fmt(length, 0)}ft exceeds the 5% resistance guideline, wasting ${fmt(pctLost, 1)}% of amplifier power as heat — upgrade to a thicker gauge.`,
      };
    },
    intro: "Thin speaker wire run over long distances quietly steals power meant for your speakers, converting it into heat inside the cable instead of sound. This calculator computes total loop resistance, dB power loss, and the percentage of amplifier wattage wasted so you can choose the right gauge before wiring your home theater.",
    sections: [
      { h: "Why loop resistance uses double the length", body: "Current travels out to the speaker and back to the amplifier through a second conductor, so total resistance is calculated using twice the one-way distance — a 50ft run actually uses 100ft of wire resistance in the loop." },
      { h: "The 5% resistance rule", body: "Audio engineers recommend keeping cable resistance under 5% of the speaker's rated impedance to avoid audible power loss and damping factor degradation, which can make bass sound loose and undefined." },
      { h: "Reading dB power loss", body: "A loss of 0.5dB or less is generally inaudible, while losses above 1dB become noticeable, especially at low frequencies where damping factor matters most for tight, controlled bass response." },
      { h: "Choosing gauge for long runs", body: "For runs under 50 feet, 16 AWG is usually adequate for 8-ohm speakers, but runs beyond 80-100 feet or 4-ohm speakers should step up to 12 or 14 AWG to keep resistance in the safe zone." },
    ],
    staticTable: {
      title: "Resistance per foot by AWG gauge",
      head: ["Gauge", "Resistance/ft (Ω)", "Best use case"],
      rows: [
        ["12 AWG", "0.00159", "Long runs 80ft+, 4-ohm speakers"],
        ["14 AWG", "0.00253", "Most home theater runs to 80ft"],
        ["16 AWG", "0.00402", "Short-medium runs under 50ft"],
        ["18 AWG", "0.00639", "In-wall short runs under 25ft"],
        ["10 AWG", "0.00100", "Subwoofer/high-current runs"],
        ["20 AWG", "0.01015", "Not recommended for speakers"],
      ],
    },
    faqs: [
      { q: "What gauge speaker wire do I need for 50 feet?", a: "For 8-ohm speakers at 50 feet, 16 AWG is typically sufficient; for 4-ohm speakers or runs over 75 feet, upgrade to 14 or 12 AWG to stay under the 5% resistance guideline." },
      { q: "Does thicker wire really improve sound quality?", a: "Thicker wire reduces resistance and power loss, which preserves damping factor and bass tightness on long runs — but on short runs under 20 feet the difference is often inaudible." },
      { q: "What is damping factor and why does wire gauge affect it?", a: "Damping factor is the amplifier's ability to control speaker cone movement; added wire resistance reduces this control, which can make bass sound loose or boomy on underpowered gauges." },
      { q: "Is 1dB of power loss noticeable?", a: "Yes, most listeners can perceive a 1dB loss as slightly quieter or less punchy sound, though it varies by listener and music content." },
      { q: "Why do 4-ohm speakers need thicker wire than 8-ohm?", a: "Lower impedance speakers draw more current for the same power, and the wire's fixed resistance represents a larger percentage of a smaller total impedance, so thicker wire is needed to stay under 5%." },
      { q: "Can I use lamp cord instead of speaker wire?", a: "Lamp cord works technically but usually uses thinner gauge and lower-quality copper, making it unsuitable for anything beyond very short, low-power runs." },
      { q: "Does wire length matter more than gauge?", a: "Both matter equally in the resistance formula — doubling length doubles resistance just as halving gauge cross-section does, so long runs always need proportionally thicker wire." },
      { q: "What's the maximum recommended speaker wire run?", a: "Most home installs stay under 100 feet using 12-14 AWG; beyond that, consider running higher voltage 70V/100V distributed audio instead of standard low-impedance speaker wire." },
      { q: "Does oxygen-free copper (OFC) reduce resistance more than standard copper?", a: "The purity difference is negligible for resistance calculations — gauge and length dominate the resistance formula far more than copper purity claims." },
      { q: "How do I measure my exact cable run length?", a: "Measure the actual physical path the wire will travel including up walls and around corners, not just the straight-line distance between amp and speaker." },
    ],
    formula: {
      expressions: [
        "LoopResistance = 2 × Length(ft) × ResistancePerFoot",
        "ResistanceRatio% = (LoopResistance / Impedance) × 100",
        "PowerLoss(dB) = 10 × log10(Impedance / (Impedance + LoopResistance))",
      ],
      variables: [
        { name: "ResistancePerFoot", meaning: "AWG-specific copper resistance constant" },
        { name: "Impedance", meaning: "Speaker's nominal impedance in ohms" },
      ],
    },
    related: ["gaming-mouse-edpi-calculator", "monitor-viewing-distance-fov-calculator", "pcb-trace-width-calculator", "drone-lipo-flight-time-calculator"],
  },

  {
    slug: "pcb-trace-width-calculator",
    cluster: "tech",
    name: "PCB Trace Width & Current Capacity",
    h1: "PCB Trace Width vs Current Capacity Calculator (IPC-2152 Standard)",
    title: "PCB Trace Width Calculator (IPC-2152) | Free (2026)",
    description: "Calculate minimum PCB trace width instantly. Free online IPC-2152 PCB trace width calculator with live formulas and a step-by-step breakdown.",
    keywords: [
      "pcb trace width calculator",
      "ipc-2152 trace width formula",
      "trace width for 5 amps calculator",
      "pcb copper thickness current capacity",
      "pcb trace resistance calculator",
      "external internal trace width calculator",
    ],
    inputs: [
      { id: "current", label: "Continuous current", type: "number", def: "5", unit: "A", min: 0.01, step: 0.1 },
      { id: "tempRise", label: "Allowable temperature rise", type: "select", def: "10", options: [
        { value: "10", label: "10°C" }, { value: "20", label: "20°C" }, { value: "30", label: "30°C" },
      ] },
      { id: "copper", label: "Copper weight", type: "select", def: "1", options: [
        { value: "0.5", label: "0.5 oz" }, { value: "1", label: "1.0 oz" }, { value: "2", label: "2.0 oz" },
      ] },
      { id: "location", label: "Trace location", type: "select", def: "external", options: [
        { value: "external", label: "External layer" }, { value: "internal", label: "Internal layer" },
      ] },
    ],
    compute: (v) => {
      const current = n(v, "current", 5), tempRise = n(v, "tempRise", 10), copper = n(v, "copper", 1), location = s(v, "location");
      const k = location === "internal" ? 0.024 : 0.048;
      const areaMils2 = Math.pow(current / (k * Math.pow(tempRise, 0.44)), 1 / 0.725);
      const widthMils = copper > 0 ? areaMils2 / (copper * 1.378) : 0;
      const widthMm = widthMils * 0.0254;
      const resistPerInch = areaMils2 > 0 ? (717 / areaMils2) : 0;
      const voltageDrop = resistPerInch * current;
      const powerDissipation = voltageDrop * current;
      return {
        cards: [
          { label: "Minimum trace width", value: `${fmt(widthMils, 1)} mils`, sub: `${fmt(widthMm, 2)} mm`, accent: true },
          { label: "Cross-section area", value: `${fmt(areaMils2, 0)} mils²`, sub: `${location === "internal" ? "Internal" : "External"} layer` },
          { label: "Resistance per inch", value: `${fmt(resistPerInch, 4)} Ω/in`, sub: `${copper}oz copper` },
          { label: "Voltage drop", value: `${fmt(voltageDrop, 3)} V/in`, sub: `${fmt(powerDissipation, 3)} W/in dissipated` },
        ],
        table: {
          title: "Trace design breakdown",
          head: ["Metric", "Value"],
          rows: [
            ["Continuous current", `${fmt(current, 2)} A`],
            ["Allowable temp rise", `${tempRise}°C`],
            ["Copper weight", `${copper} oz`],
            ["Layer type", location === "internal" ? "Internal" : "External"],
            ["Cross-section area", `${fmt(areaMils2, 0)} mils²`],
            ["Minimum trace width", `${fmt(widthMils, 1)} mils (${fmt(widthMm, 2)} mm)`],
            ["Voltage drop per inch", `${fmt(voltageDrop, 3)} V`],
          ],
        },
        summary: `For ${fmt(current, 2)}A at ${tempRise}°C rise on ${copper}oz ${location} copper, use at least a ${fmt(widthMils, 1)} mil (${fmt(widthMm, 2)}mm) trace.`,
      };
    },
    intro: "Undersized PCB traces overheat, degrade, and can eventually fail open under sustained current, while oversized traces waste valuable board space. This calculator applies the IPC-2152 empirical formula to compute the minimum safe trace width for your current, temperature rise, copper weight, and layer location.",
    sections: [
      { h: "Why external and internal traces differ", body: "Internal traces are surrounded by insulating FR4 material and dissipate heat far less efficiently than external traces exposed to open air, so IPC-2152 uses a lower k-constant (0.024 vs 0.048) requiring roughly double the width for the same current and temperature rise." },
      { h: "Understanding the IPC-2152 curve", body: "IPC-2152 replaced the older IPC-2221 standard with more accurate empirical curves derived from thermal testing on real boards, accounting for heat spreading, board thickness, and copper pour proximity more realistically than the older simplified formula." },
      { h: "Copper weight and trace width tradeoffs", body: "Doubling copper weight (e.g. 1oz to 2oz) roughly halves the required trace width for the same current, since more copper volume conducts and dissipates heat more efficiently per unit of board area." },
      { h: "Voltage drop matters for long traces", body: "Even a correctly-sized trace for thermal safety can cause unacceptable voltage drop over long distances, especially in low-voltage logic circuits — always check voltage drop separately for traces longer than a few inches." },
    ],
    staticTable: {
      title: "IPC-2152 reference trace widths (external, 10°C rise, 1oz copper)",
      head: ["Current", "Approx. width (mils)", "Approx. width (mm)"],
      rows: [
        ["1 A", "12 mils", "0.30 mm"],
        ["2 A", "24 mils", "0.61 mm"],
        ["3 A", "37 mils", "0.94 mm"],
        ["5 A", "68 mils", "1.73 mm"],
        ["10 A", "160 mils", "4.06 mm"],
        ["15 A", "260 mils", "6.60 mm"],
      ],
    },
    faqs: [
      { q: "How wide should a PCB trace be for 5 amps?", a: "For 5A continuous current with 1oz external copper and a 10°C temperature rise, a trace width of roughly 68 mils (1.73mm) is typically required per IPC-2152." },
      { q: "Why does temperature rise allowance change trace width?", a: "Allowing a higher temperature rise (e.g. 20°C or 30°C instead of 10°C) lets the trace safely carry more current at the same width, since IPC-2152 permits more heat buildup before failure risk." },
      { q: "Is IPC-2152 more accurate than IPC-2221?", a: "Yes, IPC-2152 was developed from newer empirical thermal test data across many board configurations and generally produces more realistic (often narrower) trace widths than the older 2221 chart." },
      { q: "How much does copper weight affect trace width?", a: "Doubling copper weight from 1oz to 2oz can reduce the required trace width by roughly half for the same current and temperature rise, since more copper cross-section conducts heat and current more efficiently." },
      { q: "Do internal traces really need to be twice as wide?", a: "Approximately yes — internal traces are thermally insulated by the PCB substrate, so IPC-2152 uses a k-constant half that of external traces, roughly doubling the required width for equivalent thermal performance." },
      { q: "What temperature rise should I design for?", a: "10°C is a conservative default for most consumer electronics; 20°C is acceptable for industrial designs with adequate enclosure ventilation, while power electronics may tolerate 30°C+ with proper heatsinking." },
      { q: "How do I calculate voltage drop on a long trace?", a: "Multiply the trace's resistance-per-inch (derived from its cross-sectional area) by both the current and the total trace length in inches to get total voltage drop." },
      { q: "Does trace width calculation account for copper pours next to the trace?", a: "No, IPC-2152 assumes isolated traces; nearby copper pours or ground planes can improve heat dissipation, potentially allowing slightly narrower traces in practice." },
      { q: "What's a safe minimum trace width for signal (non-power) traces?", a: "Signal traces carrying negligible current are typically sized for manufacturing tolerance and impedance control (often 4-8 mils) rather than current capacity." },
      { q: "Can I use wider traces than calculated for extra safety margin?", a: "Yes, going wider than the IPC-2152 minimum only improves thermal margin and reduces resistance, at the cost of additional board space." },
    ],
    formula: {
      expressions: [
        "Area(mils²) = (Current / (k × TempRise^0.44))^(1/0.725)",
        "TraceWidth(mils) = Area / (CopperOz × 1.378)",
        "Resistance/in(Ω) ≈ 717 / Area(mils²)",
      ],
      variables: [
        { name: "k", meaning: "0.048 external layer, 0.024 internal layer" },
        { name: "TempRise", meaning: "Allowable temperature rise in °C" },
      ],
    },
    related: ["speaker-wire-gauge-loss-calculator", "drone-lipo-flight-time-calculator", "gaming-mouse-edpi-calculator", "monitor-viewing-distance-fov-calculator"],
  },

  {
    slug: "drone-lipo-flight-time-calculator",
    cluster: "tech",
    name: "Drone LiPo Flight Time & C-Rating",
    h1: "FPV Drone LiPo Battery C-Rating, Max Amp Draw & Flight Time Sizer",
    title: "Drone LiPo Flight Time Calculator | Free (2026)",
    description: "Calculate FPV drone flight time and max safe discharge instantly. Free online LiPo battery calculator with live formulas and a step-by-step breakdown.",
    keywords: [
      "drone lipo flight time calculator",
      "lipo c rating max discharge calculator",
      "fpv battery flight time estimator",
      "6s lipo flight time calculator",
      "lipo watt hours calculator",
      "drone battery amp draw calculator",
    ],
    inputs: [
      { id: "capacity", label: "Battery capacity", type: "number", def: "1300", unit: "mAh", min: 100, step: 50 },
      { id: "cells", label: "Cell count", type: "select", def: "4", options: [
        { value: "4", label: "4S (14.8V)" }, { value: "6", label: "6S (22.2V)" },
      ] },
      { id: "crating", label: "C-Rating", type: "number", def: "100", unit: "C", min: 1, step: 5 },
      { id: "motorCount", label: "Motor count", type: "number", def: "4", min: 1, step: 1 },
      { id: "motorAmps", label: "Avg current draw per motor", type: "number", def: "15", unit: "A", min: 0.1, step: 0.5 },
    ],
    compute: (v) => {
      const capacity = n(v, "capacity", 1300), cells = n(v, "cells", 4), crating = n(v, "crating", 100);
      const motorCount = n(v, "motorCount", 4), motorAmps = n(v, "motorAmps", 15);
      const voltage = cells * 3.7;
      const totalCurrent = motorCount * motorAmps;
      const maxContinuousAmps = (capacity / 1000) * crating;
      const flightTimeMins = totalCurrent > 0 ? ((capacity * 0.8) / totalCurrent / 1000) * 60 : 0;
      const wattHours = (capacity / 1000) * voltage;
      const overStressed = totalCurrent > maxContinuousAmps;
      const utilizationPct = maxContinuousAmps > 0 ? (totalCurrent / maxContinuousAmps) * 100 : 0;
      return {
        cards: [
          { label: "Max safe continuous discharge", value: `${fmt(maxContinuousAmps, 1)} A`, sub: `${crating}C × ${fmt(capacity, 0)}mAh`, accent: true },
          { label: "Estimated flight time", value: `${fmt(flightTimeMins, 1)} min`, sub: "To 20% safe reserve" },
          { label: "Pack energy", value: `${fmt(wattHours, 1)} Wh`, sub: `${voltage}V nominal` },
          { label: "Discharge stress", value: overStressed ? "Overstressed" : "Safe", sub: `${fmt(utilizationPct, 0)}% of max rating used` },
        ],
        table: {
          title: "Flight & discharge breakdown",
          head: ["Metric", "Value"],
          rows: [
            ["Nominal voltage", `${voltage} V`],
            ["Total motor current draw", `${fmt(totalCurrent, 1)} A`],
            ["Max continuous discharge", `${fmt(maxContinuousAmps, 1)} A`],
            ["Discharge utilization", `${fmt(utilizationPct, 0)}%`],
            ["Pack energy", `${fmt(wattHours, 1)} Wh`],
            ["Estimated flight time", `${fmt(flightTimeMins, 1)} min`],
          ],
        },
        summary: overStressed
          ? `Your ${fmt(totalCurrent, 1)}A draw exceeds the ${fmt(maxContinuousAmps, 1)}A safe continuous limit — expect voltage sag and thermal stress. Flight time: ${fmt(flightTimeMins, 1)} min.`
          : `Estimated flight time is ${fmt(flightTimeMins, 1)} minutes, drawing ${fmt(utilizationPct, 0)}% of the pack's safe continuous discharge capacity.`,
      };
    },
    intro: "FPV drone flight time and battery health both depend on staying within the LiPo's true safe continuous discharge rate, not just its advertised capacity. This calculator combines cell count, C-rating, and motor current draw to estimate flight time to a safe 20% reserve and flag packs being pushed beyond their limits.",
    sections: [
      { h: "Why manufacturer C-ratings are often optimistic", body: "Many LiPo packs are rated for burst rather than true continuous discharge, and real-world sustained C-ratings often run 20-40% lower than the printed number, especially on cheaper packs — treat printed C-ratings as an upper bound, not a target." },
      { h: "The 80% usable capacity rule", body: "Discharging a LiPo below roughly 20% remaining capacity accelerates cell degradation and risks a voltage sag crash, so flight time estimates should always be based on 80% of rated capacity, not the full mAh figure." },
      { h: "How motor count and hover throttle affect draw", body: "Total current draw scales directly with motor count and average throttle current, so a heavier quad or aggressive flying style drawing higher amps per motor will always cut flight time proportionally shorter." },
      { h: "Watt-hours as a cross-pack comparison metric", body: "Because voltage differs between 4S and 6S packs, comparing capacity in mAh alone is misleading — watt-hours (Wh) normalizes energy content across different cell counts for a fair apples-to-apples comparison." },
    ],
    staticTable: {
      title: "Typical FPV flight times by setup",
      head: ["Battery", "Motor draw", "Est. flight time"],
      rows: [
        ["4S 1300mAh 100C", "4×15A (60A total)", "~4.5 min"],
        ["6S 1300mAh 100C", "4×12A (48A total)", "~5.5 min"],
        ["6S 1500mAh 100C", "4×15A (60A total)", "~4.8 min"],
        ["4S 850mAh 100C", "4×18A (72A total)", "~1.9 min"],
        ["6S 4500mAh 25C", "4×10A (40A total)", "~13.5 min (long-range)"],
        ["4S 650mAh 120C", "4×20A (80A total)", "~2 min (freestyle)"],
      ],
    },
    faqs: [
      { q: "How long will a 6S 1300mAh LiPo fly at 20A draw per motor?", a: "With four motors drawing 20A each (80A total), an 80% usable capacity gives roughly 0.78 minutes of flight time — a stark reminder that high current draw drastically cuts flight time." },
      { q: "How do I calculate real continuous discharge from C-rating?", a: "Multiply the battery's capacity in amp-hours (mAh ÷ 1000) by its C-rating to get maximum continuous amps, e.g. a 1300mAh 100C pack supports 130A continuous discharge on paper." },
      { q: "Why is my flight time shorter than the calculator predicts?", a: "Aggressive throttle inputs, cold weather, pack age, and aerodynamic drag from FPV gear all increase real-world current draw beyond the average figure used in flight time estimates." },
      { q: "What percentage of LiPo capacity is safe to use?", a: "Stopping at roughly 20% remaining capacity (using 80% of rated mAh) protects cell longevity and avoids the steep voltage sag that occurs near full discharge." },
      { q: "Are printed C-ratings on cheap batteries accurate?", a: "Often not — many budget packs inflate C-ratings for marketing, so experienced pilots derate the printed number by 20-40% when calculating true safe continuous discharge." },
      { q: "What happens if I exceed the max continuous discharge rating?", a: "Exceeding the C-rating causes excessive voltage sag under load, accelerated cell heating, puffing, and permanently reduced capacity and lifespan over repeated cycles." },
      { q: "How do watt-hours help compare 4S vs 6S packs?", a: "Watt-hours multiply capacity by voltage, giving a true energy comparison — a 6S 1300mAh pack actually stores more usable energy than a same-capacity 4S pack due to its higher voltage." },
      { q: "Does more motors always mean shorter flight time?", a: "For the same total weight and battery, yes — more motors typically mean more total current draw, though larger multirotors also carry proportionally bigger batteries to compensate." },
      { q: "Why do long-range drones use lower C-rating batteries?", a: "Long-range builds prioritize capacity and energy density over burst power, so lower C-rated but higher-mAh packs (like 25C 4500mAh) provide longer flight times at cruising throttle." },
      { q: "Does temperature affect LiPo discharge performance?", a: "Yes, cold temperatures significantly reduce a LiPo's effective discharge capability and voltage stability, which is why racing pilots often pre-warm packs before flying in cold weather." },
    ],
    formula: {
      expressions: [
        "TotalCurrent = MotorCount × MotorAmps",
        "MaxContinuousAmps = (Capacity_mAh / 1000) × C-Rating",
        "FlightTime(min) = ((Capacity_mAh × 0.80) / TotalCurrent / 1000) × 60",
        "Wh = (Capacity_mAh / 1000) × Voltage",
      ],
      variables: [
        { name: "C-Rating", meaning: "Manufacturer continuous discharge multiplier" },
        { name: "Voltage", meaning: "Cells × 3.7V nominal" },
      ],
    },
    related: ["pcb-trace-width-calculator", "speaker-wire-gauge-loss-calculator", "gaming-mouse-edpi-calculator", "monitor-viewing-distance-fov-calculator"],
  },
];
